import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Menu, X } from 'lucide-react';
import { LOGO_URL, PHONE_TEL, PHONE_DISPLAY } from '../data/content';

const LINKS = [
  { to: '/', label: 'Ana Sayfa' },
  { to: '/hizmetler', label: 'Hizmetler' },
  { to: '/hakkimizda', label: 'Hakkımızda' },
  { to: '/iletisim', label: 'İletişim' },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
        scrolled || open
          ? 'bg-[#0A1128]/80 backdrop-blur-xl border-[#1E293B]'
          : 'bg-transparent border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 group">
          <img
            src="/logo.png"
            alt="Vira Nakliyat"
            className="h-9 md:h-10 w-auto object-contain transition-transform duration-500 group-hover:scale-[1.04]"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9" data-testid="nav-desktop-links">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === '/' ? 'home' : l.to.slice(1)}`}
              className={({ isActive }) =>
                `relative text-[13px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 ${
                  isActive ? 'text-[#FF5C00]' : 'text-[#A0ABC0] hover:text-[#F2F4F8]'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={PHONE_TEL}
            data-testid="nav-call-button"
            className="hidden sm:inline-flex items-center gap-2.5 bg-[#FF5C00] hover:bg-[#E65000] text-[#0A1128] font-bold text-sm tracking-wide rounded-full pl-4 pr-5 py-2.5 transition-colors duration-300"
          >
            <Phone size={15} strokeWidth={2.5} />
            Hemen Ara
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-[#F2F4F8] p-2"
            aria-label="Menü"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#0A1128]/95 backdrop-blur-xl border-t border-[#1E293B]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.to === '/' ? 'home' : l.to.slice(1)}`}
                  className={({ isActive }) =>
                    `font-display text-2xl tracking-tight ${isActive ? 'text-[#FF5C00]' : 'text-[#F2F4F8]'}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={PHONE_TEL}
                data-testid="nav-mobile-call-button"
                className="inline-flex items-center justify-center gap-2 bg-[#FF5C00] text-[#0A1128] font-bold rounded-full py-3.5 mt-2"
              >
                <Phone size={16} strokeWidth={2.5} /> {PHONE_DISPLAY}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
